<template>
  <main>
    <!-- Core-Media Hero Banner - Carousel - Reels -->
    <lazy-cms-section v-if="eSpots?.[0]" :section="eSpots[0]" />
    <!-- Updated Static Content for customer -->
    <template v-if="$feature.enableLoyaltyLPUpdatePtc">
      <div class="px-4 py-12 md:px-8 md:pb-20 md:pt-10">
        <base-lazy-hydrate when="interaction">
          <h1 class="mb-8 text-center subtitle-1">
            {{ topSections.title }}
          </h1>
          <base-grid :cols="{ sm: 1, lg: 3 }" gap="md">
            <vf-tile
              v-for="(tile, key) in topSections.tiles"
              :key
              v-bind="tile"
              disabled
              spacing="lg"
            >
              <template #icon>
                <vf-icon class="m-3" :name="tile.icon" size="2xl" />
              </template>
              <template #title>
                <span class="mt-4 block subtitle-2 md:mt-6">
                  {{ tile.title }}
                </span>
              </template>
              <template #description>
                <span class="mx-4 mt-1 block body-3 md:mx-8">
                  {{ tile.description }}
                </span>
              </template>
            </vf-tile>
          </base-grid>
        </base-lazy-hydrate>

        <div class="f-col items-center gap-4">
          <vf-button v-if="!auth.loggedIn" class="mt-8" variant="secondary" @click="openSignInPanel">
            {{ ctaTitle }}
          </vf-button>
          <template v-else>
            <p class="mt-8 text-center">
              {{ $t.getCommunityAdvantages }}
            </p>
            <div class="flex gap-4">
              <vf-button variant="secondary" @click="navigateTo('/account')">
                {{ ctaAccountTitle }}
              </vf-button>
              <vf-button variant="secondary" @click="navigateTo('/account/loyalty')">
                {{ ctaDashboardTitle }}
              </vf-button>
            </div>
          </template>
        </div>
      </div>
      <div class="px-4 py-12 md:flex md:px-38 md:pb-20 md:pt-8">
        <div class="md:w-1/2 md:flex md:items-center">
          <div>
            <h3 id="rewards-title" class="mb-6 subtitle-1 md:mb-8 <md:text-center <md:subtitle-2">
              {{ newRewards.title }}
            </h3>
            <p id="rewards-subtitle" class="subtitle-4 <md:text-center">
              {{ newRewards.subtitle }}
            </p>
            <p id="rewards-text" class="md:ws-pre-wrap <md:text-center">
              {{ newRewards.text }}
            </p>
          </div>
        </div>
        <div class="md:w-1/2 <md:pt-8">
          <table aria-describedby="rewards-subtitle rewards-text" aria-labelledby="rewards-title" class="w-full text-left">
            <tr
              v-for="(tr, key) in rewards.newTable"
              :key
              :class="[
                { 'b-b': +key < rewards.newTable.length - 1 },
                key === 0 ? 'subtitle-2' : 'b-grey-70 brand-1 body-3',
              ]"
            >
              <component
                :is="key ? 'td' : 'th'"
                v-for="(td, tdKey) in tr"
                :key="tdKey"
                class="px-4 py-5 text-center"
                :class="[key ? 'fw-normal' : 'fw-bold']"
              >
                {{ td }}
              </component>
            </tr>
          </table>
        </div>
      </div>
    </template>
    <section v-else v-style:p="{ sm: '2rem 1rem 4rem 1rem', md: '4rem 5.5rem 2.5rem 2.5rem' }" class="container">
      <!-- Rewards section -->
      <div class="md:flex">
        <div class="md:w-1/2 md:flex md:items-center">
          <div>
            <h3 id="rewards-title" class="mb-4 title-3">
              {{ rewards.title }}
            </h3>
            <p id="rewards-subtitle" class="mb-6 subtitle-4">
              {{ rewards.subtitle }}
            </p>
            <p id="rewards-text" class="ws-pre-wrap">
              {{ rewards.text }}
            </p>
          </div>
        </div>
        <div class="md:w-1/2 <md:pt-8">
          <table aria-describedby="rewards-subtitle rewards-text" aria-labelledby="rewards-title" class="w-full text-left">
            <tr
              v-for="(tr, key) in rewards.table"
              :key
              :class="key === rewards.table.length - 1 ? '' : (key ? 'b-b' : 'b-b-2 b-t-2 uppercase')"
            >
              <component
                :is="key ? 'td' : 'th'"
                v-for="(td, tdKey) in tr"
                :key="tdKey"
                class="py-2"
                :class="{
                  'text-center': +tdKey < tr.length - 1,
                  'text-right': tdKey === tr.length - 1 }"
              >
                {{ td }}
              </component>
            </tr>
          </table>
        </div>
      </div>
      <div class="mt-16 b-b-2 b-t-2">
        <vf-accordion
          v-for="({ title, olElements }, key) in rewards.accordions"
          :key
          class-title="subtitle-2"
          size="sm"
          :title
          :value="`${key}`"
          variant="aligned"
        >
          <div class="py-6 text-sm md:flex md:space-x-3 <md:space-y-6">
            <div v-for="({ liElements, title: olTitle }, olKey) in olElements" :key="olKey" class="md:w-1/2">
              <h3 class="mb-2 b-b pb-2 subtitle-3">
                {{ olTitle }}
              </h3>
              <ol class="w-full list-decimal-inside">
                <li v-for="(li, liKey) in liElements" :key="liKey">
                  <base-interpolator :content="li">
                    {{ li }}
                    <template #link="{ args: [message, to] }">
                      <vf-link target="_blank" :to>
                        {{ message }}
                      </vf-link>
                    </template>
                  </base-interpolator>
                </li>
              </ol>
            </div>
          </div>
        </vf-accordion>
      </div>
    </section>
    <!-- Core-Media Bottom Espots -->
    <section v-style:p="{ md: '2.5rem 5.5rem 2.5rem 2.5rem' }" class="container">
      <lazy-cms-section v-if="eSpots?.[1]" :section="eSpots?.[1]" />
    </section>
    <section v-if="$feature.enableLoyaltyLPUpdatePtc" v-style:p="{ md: '2.5rem 5.5rem 2.5rem 2.5rem' }" class="container">
      <lazy-cms-section v-if="eSpots?.[2]" :section="eSpots?.[2]" />
    </section>
  </main>
</template>

<script lang="ts" setup>
const { DialogSignIn } = useDialogsStore()
const { $gtm, $locale } = useNuxtApp()
const auth = useAuthStore()

const { unsupportedLocales = [] } = useAppConfig().pages.vansFamily
if (unsupportedLocales.includes($locale))
  throw createError({ statusCode: 404, fatal: true })

const eSpots = (await useCms().getSections.generic('family')).data.value?.content.sections
const { data, error } = await useApi().internal.getStaticPage('family')
if (!data.value) throw createError({ ...error.value!, fatal: true })
const { newRewards, ctaTitle, ctaAccountTitle, ctaDashboardTitle, topSections, rewards } = data.value

const openSignInPanel = () => {
  $gtm.push('topNav.onClickUtilityLink', 'Sign In')
  DialogSignIn.open({ formLocation: 'modal:single:header:none' })
}
</script>
